import { useState } from 'react';
import { practiceResources } from '@/mocks/resources';

const levelColor: Record<string, string> = {
  'All Levels': 'bg-[#00D9FF]/10 text-[#00D9FF]',
  Beginner: 'bg-emerald-500/10 text-emerald-400',
  Intermediate: 'bg-amber-500/10 text-amber-400',
  Advanced: 'bg-red-500/10 text-red-400',
};

const PracticeResourcesSection = () => {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section id="practice-resources" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#00D9FF] text-xs font-bold tracking-widest uppercase mb-3">
            Learning Library
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mb-4">
            Practice Resources
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
            From beginner-friendly guides to advanced tooling references — everything you need to build real cybersecurity expertise.
          </p>
        </div>

        {/* Resource Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {practiceResources.map((res) => (
            <div
              key={res.id}
              className="rounded-2xl overflow-hidden border border-gray-100 cursor-pointer group hover:-translate-y-1 transition-transform duration-300"
              onMouseEnter={() => setHovered(res.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Image */}
              <div className="relative w-full h-[140px] overflow-hidden">
                <img
                  src={res.image}
                  alt={res.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 to-transparent"></div>
                <div className={`absolute top-3 left-3 w-9 h-9 rounded-lg bg-gradient-to-br ${res.color} flex items-center justify-center`}>
                  <i className={`${res.icon} text-white text-base`}></i>
                </div>
                <span className="absolute bottom-3 right-3 text-white text-xs font-semibold bg-black/40 px-2 py-0.5 rounded-full">
                  {res.count}
                </span>
              </div>

              {/* Info */}
              <div className="p-5 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">{res.type}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${levelColor[res.level]}`}>
                    {res.level}
                  </span>
                </div>
                <h3 className="text-[#0A1628] font-bold text-sm mb-2 leading-snug">{res.title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed mb-4">{res.description}</p>
                <div
                  className={`flex items-center gap-1 text-xs font-semibold transition-all duration-200 ${
                    hovered === res.id ? 'text-[#00D9FF] gap-2' : 'text-gray-400'
                  }`}
                >
                  Access Resource <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button className="px-8 py-3 rounded-full bg-[#0A1628] text-white text-sm font-semibold hover:bg-[#0F2A4A] transition-colors duration-200 cursor-pointer whitespace-nowrap">
            Browse Full Library <i className="ri-arrow-right-line ml-1"></i>
          </button>
        </div>
      </div>
    </section>
  );
};

export default PracticeResourcesSection;
